import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { ScheduleDataStatus } from "../partial/ScheduleDataStatusIndicator.component";
import {
  filterPlaceSchedulesForEnabledTypes,
  type PlaceScheduleTypeVisibility
} from "../utils/resource-booking-schedule.utils";

/** Visit-only workplace rows come from `/booking-schedule/visit-only`; strip any that still appear on place data. */
export function placeScheduleWithoutVisitOnly(
  placeScheduleData: BookingScheduleInterface[] | undefined
): BookingScheduleInterface[] {
  return (placeScheduleData ?? []).filter(s => s.isVisitOnly !== true);
}

export function colleaguePlaceSchedules(
  placeScheduleData: BookingScheduleInterface[] | undefined,
  visibility: PlaceScheduleTypeVisibility
): BookingScheduleInterface[] {
  return filterPlaceSchedulesForEnabledTypes(
    placeScheduleWithoutVisitOnly(placeScheduleData),
    visibility
  );
}

/**
 * @param placeZoneScheduleDataStatus: ScheduleDataStatus is deprecated for now
 * @param placeZoneScheduleDataForEnterprisaStatus: ScheduleDataStatus is deprecated for now
 * @param hasVehicleScheduleEnabled When false, vehicle schedule is not fetched (no license); vehicle status is ignored for combined status.
 * @param visitorManagementEnabled When false, visit-only schedule is not fetched; visit-only status is ignored for combined status.
 */
export const updateScheduleDataStatus = ({
  enabled: {
    placeScheduleEnabled,
    hasVehicleLicense,
    hasConferenceLicense,
    hasResourceLicense,
    visitorManagementEnabled
  },
  status: {
    isScheduleLoading,
    placeScheduleDataStatus,
    visitOnlyScheduleDataStatus,
    noPlaceZoneBookingScheduleDataStatus,
    vehicleScheduleDataStatus,
    resourceScheduleDataStatus,
    setScheduleDataStatus
  }
}: {
  enabled: {
    /** True when workplace meta or parking/EV placeTypes require `/booking-schedule/place`. */
    placeScheduleEnabled: boolean;
    hasVehicleLicense: boolean;
    hasConferenceLicense: boolean;
    hasResourceLicense: boolean;
    visitorManagementEnabled: boolean;
  };
  status: {
    isScheduleLoading: boolean;
    placeScheduleDataStatus: ScheduleDataStatus;
    visitOnlyScheduleDataStatus: ScheduleDataStatus;
    noPlaceZoneBookingScheduleDataStatus: ScheduleDataStatus;
    vehicleScheduleDataStatus: ScheduleDataStatus;
    resourceScheduleDataStatus: ScheduleDataStatus;
    setScheduleDataStatus: (s: ScheduleDataStatus) => void;
  };
}) => {
  const placeLoading = placeScheduleEnabled && placeScheduleDataStatus === "loading";
  const placeError = placeScheduleEnabled && placeScheduleDataStatus === "error";
  const placeSuccess = !placeScheduleEnabled || placeScheduleDataStatus === "success";

  const vehicleLoading = hasVehicleLicense && vehicleScheduleDataStatus === "loading";
  const vehicleError = hasVehicleLicense && vehicleScheduleDataStatus === "error";
  const vehicleSuccess = !hasVehicleLicense || vehicleScheduleDataStatus === "success";

  const visitOnlyLoading = visitorManagementEnabled && visitOnlyScheduleDataStatus === "loading";
  const visitOnlyError = visitorManagementEnabled && visitOnlyScheduleDataStatus === "error";
  const visitOnlySuccess = !visitorManagementEnabled || visitOnlyScheduleDataStatus === "success";

  const conferenceLoading =
    hasConferenceLicense && noPlaceZoneBookingScheduleDataStatus === "loading";
  const conferenceError = hasConferenceLicense && noPlaceZoneBookingScheduleDataStatus === "error";
  const conferenceSuccess =
    !hasConferenceLicense || noPlaceZoneBookingScheduleDataStatus === "success";

  const resourceLoading = hasResourceLicense && resourceScheduleDataStatus === "loading";
  const resourceError = hasResourceLicense && resourceScheduleDataStatus === "error";
  const resourceSuccess = !hasResourceLicense || resourceScheduleDataStatus === "success";

  if (placeLoading || conferenceLoading || vehicleLoading || resourceLoading || isScheduleLoading)
    return setScheduleDataStatus("loading");

  if (placeError || conferenceError || vehicleError || resourceError)
    return setScheduleDataStatus("error");

  if (placeSuccess && conferenceSuccess && vehicleSuccess && resourceSuccess)
    return setScheduleDataStatus("success");
  if (
    placeScheduleDataStatus === "loading" ||
    visitOnlyLoading ||
    conferenceLoading ||
    vehicleLoading ||
    resourceLoading ||
    isScheduleLoading
  )
    return setScheduleDataStatus("loading");

  if (
    placeScheduleDataStatus === "error" ||
    visitOnlyError ||
    conferenceError ||
    vehicleError ||
    resourceError
  )
    return setScheduleDataStatus("error");

  if (
    placeScheduleDataStatus === "success" &&
    visitOnlySuccess &&
    conferenceSuccess &&
    vehicleSuccess &&
    resourceSuccess
  )
    return setScheduleDataStatus("success");
};

export const updateScheduleData = (
  placeScheduleData: BookingScheduleInterface[] | undefined,
  visitOnlyScheduleData: BookingScheduleInterface[] | undefined,
  noPlaceZoneBookingScheduleData: BookingScheduleInterface[] | undefined, // conference room
  vehicleScheduleData: BookingScheduleInterface[] | undefined,
  resourceScheduleData: BookingScheduleInterface[] | undefined,
  setSchedules: (s: BookingScheduleInterface[]) => void,
  placeScheduleVisibility: PlaceScheduleTypeVisibility
) => {
  const place = colleaguePlaceSchedules(placeScheduleData, placeScheduleVisibility);
  const visitOnly = visitOnlyScheduleData ?? [];
  const noPlace = noPlaceZoneBookingScheduleData ?? [];
  const vehicle = vehicleScheduleData ?? [];
  const resource = resourceScheduleData ?? [];
  setSchedules([...place, ...visitOnly, ...noPlace, ...vehicle, ...resource]);
};
